/**
 * Netting tolerance for billing carve adjustments. Canonical home for the
 * calibration; `packages/core/src/recon/reconConstants.ts` duplicates the
 * numbers because `revlock-core` depends on `revlock-accounting` and not the
 * reverse, so the engine cannot import the recon constants. The duplication is
 * pinned by `carveTolerance.pure.test.ts`, which asserts the recon's generated
 * SQL rather than the two exported constants.
 *
 * Calibration: rounding each line's target billing independently to cents means
 * the carve column need not sum to exactly zero. A four-line order with a
 * repeating allocation percentage produces residuals of +/-0.01, so a tolerance
 * tighter than the rounding dust would make the engine warn on its own correct
 * output.
 */

export const CARVE_TOLERANCE_FLOOR = 0.05;

export const CARVE_TOLERANCE_PER_LINE = 0.01;

/** `GREATEST(0.05, 0.01 x line count)` -- the SQL form lives in the recon. */
export function carveTolerance(lineCount: number): number {
    return Math.max(
        CARVE_TOLERANCE_FLOOR,
        CARVE_TOLERANCE_PER_LINE * lineCount
    );
}

export const CARVE_AMOUNT_SCALE = 2;

/** `Cumulative Allocation Percent` is `DECIMAL(14,8)`; it is stored as a fraction. */
export const CARVE_PERCENT_SCALE = 8;

/**
 * Upper bound on a line's share of its pool. A pool whose lines nearly cancel
 * has a denominator near zero and produces shares in the thousands, so every
 * target billing derived from it is nonsense.
 *
 * `Cumulative_Allocation_Percent` is `DECIMAL(14,8)`, so a share above
 * 999999.99999999 fails the warehouse load loudly. This bound exists for the
 * quiet band below that ceiling: nonsense numbers that store cleanly.
 *
 * Loose on purpose, because the skip is silent and a false positive stops
 * billing redistribution for a real contract. A partially credited term
 * (+1000 against a -900 credit) legitimately reaches 10x.
 */
export const CARVE_MAX_ALLOCATION_SHARE = 100;
